/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Accordion,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DANGER,
  Button,
  TOAST_TYPE_SUCCESS,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { CORE_STRINGS } from '../../../../Constants/Strings.constants';
import { TenantSettingsServiceDeleteSettingDto, getTenantSettingsService } from '../../../../Services/tenant-settings';
import { SETTING_LIST_DATA_IDS } from '../../SettingList.constants';
import { SettingDeleteProps } from './SettingDelete.props';

/**
 * The button for deleting a setting.
 */
export const SettingDelete = (props: SettingDeleteProps): JSX.Element => {
  const { t } = useTranslation();
  const { clearCache, closeDialog, isBusy, setFormError, setIsBusy, setting } = props;
  const { schemaId, serviceId, settingKey } = setting;

  const tenantSettingsService = getTenantSettingsService();

  const { updateActiveToast } = useToastStore();

  const [performingAction, setPerformingAction] = useState<boolean>(false);

  // Labels
  const labels = useMemo(() => {
    return {
      button: t(CORE_STRINGS.deleteSetting),
      changesImmediate: t(CORE_STRINGS.changesImmediate),
      failure: t(CORE_STRINGS.deleteSettingFailure),
      header: t(CORE_STRINGS.deleteThisSetting),
      message: t(CORE_STRINGS.deleteSettingMessage),
      success: t(CORE_STRINGS.deleteSettingSuccess),
    };
  }, [t]);

  const submitHandler = useCallback(() => {
    setIsBusy(true);

    const dto: TenantSettingsServiceDeleteSettingDto = {
      schemaId,
      serviceId,
      settingKey,
    };

    // Success toast props
    const toastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: labels.success,
      toastType: TOAST_TYPE_SUCCESS,
    };

    lastValueFrom(tenantSettingsService.deleteSetting(dto))
      .then(() => {
        updateActiveToast(<Toast {...toastProps} data-id={SETTING_LIST_DATA_IDS.actionDeleteSettingSuccess} />);
        clearCache(new Date().toISOString());
        closeDialog && closeDialog();
        return true;
      })
      .catch(() => {
        setFormError(labels.failure);
        setPerformingAction(false);
        setIsBusy(false);
      });
  }, [
    clearCache,
    closeDialog,
    labels,
    schemaId,
    serviceId,
    setFormError,
    setIsBusy,
    setPerformingAction,
    settingKey,
    tenantSettingsService,
    updateActiveToast,
  ]);

  useEffect(() => {
    // Form submission check
    if (performingAction && !isBusy) {
      submitHandler();
    }
  }, [isBusy, performingAction, submitHandler]);

  const accordionBody = (
    <>
      <span>{labels.message}</span>
      <span className="service-settings__warning">{labels.changesImmediate}</span>
      <Button
        data-id={SETTING_LIST_DATA_IDS.actionDeleteSettingConfirm}
        disabled={isBusy}
        format={BUTTON_FORMAT_STRONG}
        isLoading={performingAction}
        onClick={(): void => setPerformingAction(true)}
        text={labels.button}
        theme={BUTTON_THEME_DANGER}
      />
    </>
  );

  return (
    <div className="service-settings__delete">
      <Accordion body={accordionBody} data-id={SETTING_LIST_DATA_IDS.actionDeleteSetting} header={labels.header} />
    </div>
  );
};
